/**
 * Motion tokens.
 *
 * Single source of truth for animation timing. Components SHOULD consume
 * these instead of writing literals so the reduced-motion override works
 * uniformly. The retro-world FPS / frame counts (v2) live alongside chrome
 * timings on purpose — same governing rule, same kill switch.
 *
 * Values mirror the CSS custom properties in `src/styles/globals.css`
 * (Apple-inspired animation tokens from the shared.css mockup).
 *
 * Reduced-motion contract: when `prefers-reduced-motion: reduce` is set,
 * `prefersReducedMotion()` returns `true` and consumers should:
 *   - freeze sprites on idle frame 1
 *   - replace tool-call arc animations with instant icon swaps
 *   - shorten or eliminate chrome easing (we keep durations but lower the
 *     visual effect by reducing scale/translate amplitudes)
 */

export const DURATION = Object.freeze({
  /** Hover, ripple, press feedback. */
  instant: 80,
  /** Buttons, badge state changes, tooltip in. */
  fast: 150,
  /** Modal / drawer enter, message appear. */
  normal: 250,
  /** Card hover, toggle switch. */
  moderate: 350,
  /** Page transitions, large overlays. */
  slow: 420,
});

export const EASING = Object.freeze({
  /** Default chrome easing — smooth deceleration. */
  default: 'cubic-bezier(0.4, 0, 0.2, 1)',
  /** Spring — entrance animations, stagger cards. */
  spring: 'cubic-bezier(0.2, 1, 0.3, 1)',
  /** Bounce — playful overshoot for interactive elements. */
  bounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  /** Enter — decelerate on entrance. */
  enter: 'cubic-bezier(0.0, 0.0, 0.2, 1.0)',
  /** Exit — accelerate on exit. */
  exit: 'cubic-bezier(0.4, 0, 1, 1)',
  /** Sheet — bottom sheet / modal slide-up. */
  sheet: 'cubic-bezier(0.32, 0.72, 0, 1)',
});

export const DELAY = Object.freeze({
  /** Tooltip show delay to avoid flicker. */
  tooltip: 600,
  /** Stagger delay between sequential card/item entrances. */
  stagger: 40,
});

/** Apple-style press feedback (scale down on active). */
export const APPLE_PRESS = Object.freeze({
  scale: 0.97,
  duration: 80,
  easing: 'linear',
});

/** Apple-style release feedback (bounce back). */
export const APPLE_RELEASE = Object.freeze({
  duration: 350,
  easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
});

/** v2 retro-world only. */
export const SPRITE_MOTION = Object.freeze({
  fps: 8,
  framesByState: Object.freeze({
    idle: 2,
    thinking: 4,
    working: 6,
    success: 3,
    error: 2,
    walking: 4,
  }),
});

/**
 * Returns whether the current user prefers reduced motion. Uses the
 * `matchMedia` query at call time so consumers don't have to memoize. Safe to
 * call during render. SSR-safe (returns `false` outside the browser).
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
